import { Controller } from "@hotwired/stimulus"
import { Editor } from '@tiptap/core'
import StarterKit from '@tiptap/starter-kit'
import Highlight from '@tiptap/extension-highlight'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Image from '@tiptap/extension-image'
import { TextStyle } from '@tiptap/extension-text-style'
import FontFamily from '@tiptap/extension-font-family'
import { Color } from '@tiptap/extension-color'
import Bold from '@tiptap/extension-bold'

export default class extends Controller {
  static targets = [
    "editor",
    "colorPicker",
    "content",
    "copyButton",
    "copyIcon",
    "checkIcon",
    "linkPopup",
    "linkInput"
  ]
  static values = {
    settings: Object,
    syncToInput: String
  }

  connect() {
    this.initializeEditor()
    this.boundDocumentClickHandler = this.handleDocumentClick.bind(this)
    this.boundKeydownHandler = this.handleLinkInputKeydown.bind(this)
    this.boundSetContentHandler = this.handleSetContent.bind(this)
    document.addEventListener("text-editor:set-content", this.boundSetContentHandler)
  }

  disconnect() {
    document.removeEventListener("text-editor:set-content", this.boundSetContentHandler)
    if (this.editor) {
      this.editor.destroy()
    }
  }

  handleSetContent(event) {
    const { html } = event.detail
    if (this.editor && html) {
      this.editor.commands.setContent(html)
    }
  }

  getContent() {
    if (this.hasContentTarget) {
      try {
        return JSON.parse(this.contentTarget.textContent)
      } catch (e) {
        console.error('Failed to parse content JSON:', e)
        return '<p>Start typing...</p>'
      }
    }
    return '<p>Start typing...</p>'
  }

  initializeEditor() {
    const FontSizeTextStyle = TextStyle.extend({
      addAttributes() {
        return {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize,
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {}
              }
              return { style: 'font-size: ' + attributes.fontSize }
            },
          },
        }
      },
    })

    const CustomBold = Bold.extend({
      renderHTML({ mark, HTMLAttributes }) {
        const { style, ...rest } = HTMLAttributes
        const newStyle = 'font-weight: bold;' + (style ? ' ' + style : '')
        return [ 'span', { ...rest, style: newStyle.trim() }, 0 ]
      },
      addOptions() {
        return {
          ...this.parent?.(),
          HTMLAttributes: {},
        }
      },
    })

    const extensions = [
      StarterKit.configure({
        textStyle: false,
        bold: false,
        italic: this.settingsValue.italic !== false,
        strike: this.settingsValue.strike !== false,
        code: this.settingsValue.code !== false,
        heading: this.settingsValue.format !== false,
        bulletList: this.settingsValue.list !== false,
        orderedList: this.settingsValue.ordered_list !== false,
        blockquote: this.settingsValue.blockquote !== false,
        horizontalRule: this.settingsValue.horizontal_rule !== false,
        marks: {
          bold: false,
        },
      }),
    ]

    if (this.settingsValue.bold !== false) {
      extensions.push(CustomBold)
    }

    extensions.push(TextStyle)

    if (this.settingsValue.text_color !== false) {
      const CustomColor = Color.extend({
        renderHTML({ mark, HTMLAttributes }) {
          const { style, ...rest } = HTMLAttributes
          return ['span', { ...rest, style: `color: ${mark.attrs.color}${style ? '; ' + style : ''}` }, 0]
        },
        addOptions() {
          return {
            ...this.parent?.(),
            types: ['textStyle'],
          }
        },
      })
      extensions.push(CustomColor)
    }

    if (this.settingsValue.text_size !== false) {
      extensions.push(FontSizeTextStyle)
    }

    if (this.settingsValue.font_family !== false) {
      extensions.push(FontFamily)
    }

    if (this.settingsValue.highlight !== false) {
      extensions.push(Highlight)
    }

    if (this.settingsValue.underline !== false) {
      extensions.push(Underline)
    }

    if (this.settingsValue.link !== false) {
      const CustomLink = Link.extend({
        addAttributes() {
          return {
            ...this.parent?.(),
            style: {
              default: null,
              parseHTML: element => element.getAttribute('style'),
              renderHTML: attributes => {
                if (!attributes.style) {
                  return {}
                }
                return { style: attributes.style }
              },
            },
          }
        },
        renderHTML({ HTMLAttributes }) {
          return ['a', HTMLAttributes, 0]
        },
      })

      extensions.push(CustomLink.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }))
    }

    if (this.settingsValue.text_align !== false) {
      extensions.push(TextAlign.configure({
        types: [ 'heading', 'paragraph' ],
      }))
    }

    if (this.settingsValue.image !== false) {
      extensions.push(Image)
    }

    this.editor = new Editor({
      element: this.editorTarget,
      extensions: extensions,
      content: this.getContent(),
      editorProps: {
        attributes: {
          class: 'format focus:outline-none format-blue max-w-none bg-gray-100 text-gray-500',
        },
      },
      onCreate: () => this.syncContentToInput(),
      onUpdate: () => this.syncContentToInput()
    })
  }

  syncContentToInput() {
    if (!this.hasSyncToInputValue) return

    const input = document.getElementById(this.syncToInputValue)
    if (input) {
      input.value = this.editor.getHTML()
    }
  }

  toggleBold() {
    this.editor.chain().focus().toggleBold().run()
  }

  toggleItalic() {
    this.editor.chain().focus().toggleItalic().run()
  }

  toggleUnderline() {
    this.editor.chain().focus().toggleUnderline().run()
  }

  toggleStrike() {
    this.editor.chain().focus().toggleStrike().run()
  }

  toggleHighlight() {
    const isHighlighted = this.editor.isActive('highlight')
    this.editor.chain().focus().toggleHighlight({
      color: isHighlighted ? undefined : '#ffc078'
    }).run()
  }

  toggleLink() {
    const linkAttributes = this.editor.getAttributes('link')

    if (linkAttributes.href) {
      this.editor.chain().focus().unsetLink().run()
    } else {
      this.showLinkPopup()
    }
  }

  removeLink() {
    this.editor.chain().focus().unsetLink().run()
  }

  toggleCode() {
    this.editor.chain().focus().toggleCode().run()
  }

  setTextAlign(event) {
    const align = event.currentTarget.dataset.align
    this.editor.chain().focus().setTextAlign(align).run()
  }

  toggleList() {
    this.editor.chain().focus().toggleBulletList().run()
  }

  toggleOrderedList() {
    this.editor.chain().focus().toggleOrderedList().run()
  }

  toggleBlockquote() {
    this.editor.chain().focus().toggleBlockquote().run()
  }

  toggleHorizontalRule() {
    this.editor.chain().focus().setHorizontalRule().run()
  }

  addImage() {
    const url = window.prompt('Enter image URL:', 'https://placehold.co/600x400')
    if (url) {
      this.editor.chain().focus().setImage({ src: url }).run()
    }
  }

  setParagraph() {
    this.editor.chain().focus().setParagraph().run()
    this.hideDropdownByType('typography')
  }

  setHeading(event) {
    const level = parseInt(event.currentTarget.dataset.headingLevel)
    this.editor.chain().focus().toggleHeading({ level }).run()
    this.hideDropdownByType('typography')
  }

  setTextSize(event) {
    const fontSize = event.currentTarget.dataset.textSize
    this.editor.chain().focus().setMark('textStyle', { fontSize }).run()
    this.hideDropdownByType('textSize')
  }

  setColor(event) {
    const selectedColor = event.target.value
    this.applyColorToSelection(selectedColor)
  }

  setHexColor(event) {
    const selectedColor = event.currentTarget.dataset.hexColor
    this.applyColorToSelection(selectedColor)
  }

  applyColorToSelection(color) {
    const { from, to } = this.editor.state.selection

    const linkMark = this.editor.getAttributes('link')
    if (linkMark.href) {
      this.editor.chain().focus().updateAttributes('link', {
        style: `color: ${color}`
      }).run()
    } else {
      this.editor.chain().focus().setColor(color).run()
    }
  }

  resetColor() {
    const linkMark = this.editor.getAttributes('link')
    if (linkMark.href) {
      this.editor.chain().focus().updateAttributes('link', {
        style: null
      }).run()
    } else {
      this.editor.commands.unsetColor()
    }
  }

  setFontFamily(event) {
    const fontFamily = event.currentTarget.dataset.fontFamily
    this.editor.chain().focus().setFontFamily(fontFamily).run()
    this.hideDropdownByType('fontFamily')
  }

  copyContent() {
    if (!this.editor) return

    const htmlContent = this.editor.getHTML()
    const textContent = this.editor.getText()

    if (navigator.clipboard && window.ClipboardItem) {
      const htmlBlob = new Blob([htmlContent], { type: 'text/html' })
      const textBlob = new Blob([textContent], { type: 'text/plain' })
      const clipboardItem = new ClipboardItem({
        'text/html': htmlBlob,
        'text/plain': textBlob
      })

      navigator.clipboard.write([clipboardItem]).then(() => {
        this.showCopySuccess()
      }).catch(() => {
        this.copyTextFallback(textContent)
      })
    } else {
      this.copyTextFallback(textContent)
    }
  }

  copyTextFallback(text) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        this.showCopySuccess()
      }).catch(() => {
        this.legacyCopy(text)
      })
    } else {
      this.legacyCopy(text)
    }
  }

  legacyCopy(text) {
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    try {
      document.execCommand('copy')
      this.showCopySuccess()
    } catch (err) {
      console.error('Failed to copy text: ', err)
    } finally {
      document.body.removeChild(textArea)
    }
  }

  showCopySuccess() {
    if (this.hasCopyIconTarget && this.hasCheckIconTarget) {
      this.copyIconTarget.classList.add('hidden')
      this.checkIconTarget.classList.remove('hidden')

      setTimeout(() => {
        this.copyIconTarget.classList.remove('hidden')
        this.checkIconTarget.classList.add('hidden')
      }, 3000)
    }
  }

  showLinkPopup() {
    if (!this.hasLinkPopupTarget || !this.hasLinkInputTarget) {
      return
    }

    this.savedSelection = this.editor.state.selection

    const { from, to } = this.savedSelection
    const start = this.editor.view.coordsAtPos(from)
    const end = this.editor.view.coordsAtPos(to)

    const editorRect = this.element.getBoundingClientRect()
    const popupX = ((start.left + end.left) / 2) - editorRect.left - 144
    const popupY = start.top - editorRect.top - 80


    this.linkPopupTarget.style.left = `${Math.max(10, popupX)}px`
    this.linkPopupTarget.style.top = `${Math.max(10, popupY)}px`

    this.linkPopupTarget.classList.remove('hidden')

    const rect = this.linkPopupTarget.getBoundingClientRect()

    this.linkInputTarget.value = ''
    this.linkInputTarget.focus()

    this.linkInputTarget.addEventListener('keydown', this.boundKeydownHandler)

    setTimeout(() => {
      document.addEventListener('click', this.boundDocumentClickHandler)
    }, 10)
  }

  closeLinkPopup() {
    if (!this.hasLinkPopupTarget) return

    this.linkPopupTarget.classList.add('hidden')

    this.linkInputTarget.removeEventListener('keydown', this.boundKeydownHandler)
    document.removeEventListener('click', this.boundDocumentClickHandler)

    this.editor.view.focus()
  }

  applyLink() {
    if (!this.hasLinkInputTarget) return

    const url = this.linkInputTarget.value.trim()
    if (!url) return

    let finalUrl = url
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      finalUrl = 'https://' + url
    }

    if (this.savedSelection) {
      this.editor.view.dispatch(
        this.editor.state.tr.setSelection(this.savedSelection)
      )
    }

    this.editor.chain().focus().setLink({
      href: finalUrl,
      target: '_blank',
      rel: 'noopener noreferrer nofollow'
    }).run()

    this.closeLinkPopup()
  }

  handleLinkInputKeydown(event) {
    if (event.key === 'Enter') {
      event.preventDefault()
      this.applyLink()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      this.closeLinkPopup()
    }
  }

  handleDocumentClick(event) {
    if (!this.linkPopupTarget.contains(event.target)) {
      this.closeLinkPopup()
    }
  }

  hideDropdown(dropdownId) {
    if (window.FlowbiteInstances) {
      const dropdown = window.FlowbiteInstances.getInstance('Dropdown', dropdownId)
      if (dropdown) {
        dropdown.hide()
      }
    }
  }

  hideDropdownByType(dropdownType) {
    const dropdownElement = this.element.querySelector(`[id*="${dropdownType}Dropdown"]`)
    if (dropdownElement) {
      this.hideDropdown(dropdownElement.id)
    }
  }
}
